/*
 * Records the last real user gesture, so focus changes the customer
 * did not cause (the mobile keyboard closing) can be told apart from
 * ones they did. Read by onFocusOut below.
 */
document.addEventListener(
  'pointerdown',
  () => {
    window.pmLastPointerDown = Date.now();
  },
  true
);

document.addEventListener(
  'keydown',
  (event) => {
    if (event.key === 'Tab' || event.key === 'Enter') {
      window.pmLastFocusKey = Date.now();
    }
  },
  true
);

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.selectCollections = this.querySelectorAll('.search-colletion-item');

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('input', this.trackInputIntent.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    if (this.selectCollections && this.selectCollections.length > 0) {
      this.selectCollections.forEach(collection => {
        collection.addEventListener("click", (e) => {
          let clickedElement = e.target;
          let getNewName = clickedElement.textContent;
          let getNewCollection = clickedElement.getAttribute("data-colection-handle");
          let nameDesignation = document.querySelector('.search-collections .collections-list summary');
          let collectionsDropdown = document.querySelector('details.collections-list');
      
          this.selectCollections.forEach(item => item.classList.remove('active'));
          clickedElement.classList.add('active');
          removeStyles();
          nameDesignation.innerHTML = `<span>${getNewName}</span>`;
          collectionsDropdown.className = "collections-list";
      
          if (getNewCollection) {
            collectionsDropdown.classList.add(getNewCollection);
      
            function applyStyles() {
              let styleElement = document.createElement('style');
              styleElement.textContent = `.predictive-search__list-item:not(.${getNewCollection}), .predictive-search-suggestions { display: none; }`;
              let targetElement = document.querySelector('predictive-search.search-modal__form');
              targetElement.insertBefore(styleElement, targetElement.firstChild);
            }
      
            applyStyles();
          }
      
          collectionsDropdown.removeAttribute('open');
        });
      });
      
      function removeStyles() {
        let targetElement = document.querySelector('predictive-search.search-modal__form');
        let styleElement = targetElement.querySelector('style');
        if (styleElement) {
          let parentElement = styleElement.parentElement;
          parentElement.removeChild(styleElement);
        }
      }
    }
  
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      const focused = document.activeElement;

      if (this.contains(focused)) return;

      /*
       * Dismissing the mobile keyboard blurs the input without the
       * customer touching the page: focus drops to <body>, or iOS
       * hands it to another field. Closing on that took the results
       * away while they were still reading them. Only close when a
       * tap or a focus-moving key actually caused it — an outside
       * tap is still handled by the document pointerdown below.
       */
      const prompted =
        Math.min(
          Date.now() - (window.pmLastPointerDown || 0),
          Date.now() - (window.pmLastFocusKey || 0)
        ) < 400;

      if (!prompted) return;

      this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // ponytail: 10 is Shopify's hard max for predictive search; more needs the full /search page
    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&resources[limit]=10&resources[limit_scope]=each&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  trackInputIntent(event) {
    // Inline completion follows typing forward — never deletion, never mid-IME.
    this.canAutocomplete = !event.isComposing && !(event.inputType || '').startsWith('delete');
  }

  autocompleteInline() {
    if (!this.canAutocomplete) return;
    // Touch keyboards handle selected trailing text badly; leave them the dropdown.
    if (!window.matchMedia('(hover: hover)').matches) return;

    const typed = this.input.value;
    if (!typed || this.input.selectionStart !== typed.length) return;

    const suggestion = this.querySelector('.predictive-search__item-query-result');
    const completion = suggestion && suggestion.getAttribute('aria-label');
    if (!completion || completion.length <= typed.length) return;
    if (completion.slice(0, typed.length).toLowerCase() !== typed.toLowerCase()) return;

    // Keep the casing the user typed, select the rest so the next keystroke replaces it
    this.input.value = typed + completion.slice(typed.length);
    this.input.setSelectionRange(typed.length, completion.length);

    // Footer says Search for “aud” — make it agree with the field.
    // lastIndexOf, not replace: the term is quoted at the end, and a short term
    // like "earch" would otherwise match inside the word "Search".
    const searchFor = this.querySelector('[data-predictive-search-search-for-text]');
    if (!searchFor) return;
    const label = searchFor.textContent;
    const at = label.lastIndexOf(typed);
    if (at !== -1) {
      searchFor.textContent = label.slice(0, at) + completion + label.slice(at + typed.length);
    }
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.autocompleteInline();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

const collectionsListElement = document.querySelector('details.collections-list');
const predictiveSearchElement = document.querySelector('predictive-search.search-modal__form');

if (collectionsListElement && predictiveSearchElement) {
collectionsListElement.addEventListener('toggle', function() {
  if (this.open) {
    if (predictiveSearchElement && predictiveSearchElement.hasAttribute('open')) {
      predictiveSearchElement.removeAttribute('open');
    }
  } else {
    predictiveSearchElement.style.display = '';
  }
});

predictiveSearchElement.addEventListener('toggle', function() {
  if (this.hasAttribute('open')) {
    if (collectionsListElement && collectionsListElement.hasAttribute('open')) {
      collectionsListElement.removeAttribute('open');
    }
  }
});

predictiveSearchElement.addEventListener('focusin', function() {
  if (collectionsListElement && collectionsListElement.hasAttribute('open')) {
    collectionsListElement.removeAttribute('open');
  }
});

predictiveSearchElement.addEventListener('toggle', function() {
  if (this.hasAttribute('open')) {
    collectionsListElement.style.display = 'none';
  } else {
    collectionsListElement.style.display = '';
  }
});
}

customElements.define('predictive-search', PredictiveSearch);
/* Resting state: recent searches + popular makes, shown on focus of an empty field.
   ponytail: localStorage only — no customer account, no server round-trip. */
(function () {
  var KEY = 'pm-recent-searches';
  var LIMIT = 5;

  function read() {
    try {
      var v = JSON.parse(localStorage.getItem(KEY));
      return Array.isArray(v) ? v : [];
    } catch (e) {
      return [];
    }
  }

  function remember(term) {
    var next = read().filter(function (t) {
      return t.toLowerCase() !== term.toLowerCase();
    });
    next.unshift(term);
    try {
      localStorage.setItem(KEY, JSON.stringify(next.slice(0, LIMIT)));
    } catch (e) {
      /* private mode / quota — recents are a nicety, not a feature to fail on */
    }
  }

  document.addEventListener('submit', function (event) {
    var form = event.target.closest && event.target.closest('form[role="search"]');
    if (!form) return;
    var input = form.querySelector('input[name="q"]');
    var term = input && input.value.trim();
    if (term) remember(term);
  });

  document.querySelectorAll('predictive-search[data-resting]').forEach(function (root) {
    var input = root.querySelector('input[name="q"]');
    var panel = root.querySelector('[data-resting-panel]');
    var group = root.querySelector('[data-recent]');
    var list = root.querySelector('[data-recent-list]');
    if (!input || !panel) return;

    function render() {
      var terms = read();
      group.hidden = terms.length === 0;
      list.textContent = '';
      terms.forEach(function (term) {
        var link = document.createElement('a');
        link.className = 'predictive-search__chip';
        link.href = '/search?q=' + encodeURIComponent(term) + '&options%5Bprefix%5D=last';
        link.textContent = term; // textContent, not innerHTML — the term is user input
        var li = document.createElement('li');
        li.appendChild(link);
        list.appendChild(li);
      });
    }

    function sync() {
      if (input.value.trim() || root.hasAttribute('open')) {
        root.classList.remove('is-resting');
        return;
      }
      render();
      root.classList.add('is-resting');
    }

    input.addEventListener('focus', sync);
    input.addEventListener('input', sync);
    root.addEventListener('focusout', function () {
      setTimeout(function () {
        if (!root.contains(document.activeElement)) root.classList.remove('is-resting');
      });
    });

    var clear = root.querySelector('[data-clear-recent]');
    if (clear) {
      clear.addEventListener('click', function () {
        try { localStorage.removeItem(KEY); } catch (e) {}
        render();
        input.focus();
      });
    }
  });
})();
